import { apiClient, getToken } from './apiClient';
import type {
  BookDto,
  BookMetadataDto,
  ChapterContentDto,
  GetChapterContentInput,
  BookUploadDto,
  BookUploadResultDto,
  BookUploadStatusDto,
} from '../types/book';
import type {
  BookSearchResultDto,
  CrossBookSearchResultDto,
} from '../types/search';

const BOOKS_EXTN_API = '/api/app/books-extn';
const BOOKS_API = '/api/app/books';

export const booksService = {
  /**
   * Get all books (with pagination)
   * Used for book selection in admin features
   */
  async getAllBooks(params?: {
    skipCount?: number;
    maxResultCount?: number;
    filterText?: string;
    publisherId?: string;
    format?: number; // BookFormat enum: EPUB = 1, PDF = 2
  }): Promise<{ items: BookDto[]; totalCount: number }> {
    // API returns BookWithNavigationPropertiesDto which has { book, publisher } structure
    const response = await apiClient.get<{
      items: Array<{ book: BookDto; publisher: any }>;
      totalCount: number
    }>(
      BOOKS_API,
      { params: { maxResultCount: 1000, ...params } }
    );

    // Extract just the book objects from the navigation properties
    return {
      items: response.data.items.map(item => item.book),
      totalCount: response.data.totalCount
    };
  },
  /**
   * Upload EPUB file with metadata and optional cover image
   * Supports large files up to 3GB
   */
  async uploadEpub(data: BookUploadDto, file: File, coverImage?: File): Promise<BookDto> {
    const formData = new FormData();

    // Append metadata
    Object.entries(data).forEach(([key, value]) => {
      if (value !== undefined && value !== null) {
        formData.append(key, value.toString());
      }
    });

    // Append files
    formData.append('epubFile', file);
    if (coverImage) {
      formData.append('coverImage', coverImage);
    }

    const response = await apiClient.post<BookDto>(
      `${BOOKS_EXTN_API}/upload-epub`,
      formData,
      {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
        // Track upload progress
        onUploadProgress: (progressEvent) => {
          if (progressEvent.total) {
            const percentCompleted = Math.round(
              (progressEvent.loaded * 100) / progressEvent.total
            );
            console.log(`Upload progress: ${percentCompleted}%`);
          }
        },
      }
    );

    return response.data;
  },

  /**
   * Upload PDF file with metadata and optional cover image
   * Supports large files up to 3GB
   */
  async uploadPdf(data: BookUploadDto, file: File, coverImage?: File): Promise<BookDto> {
    const formData = new FormData();

    // Append metadata
    Object.entries(data).forEach(([key, value]) => {
      if (value !== undefined && value !== null) {
        formData.append(key, value.toString());
      }
    });

    // Append files
    formData.append('pdfFile', file);
    if (coverImage) {
      formData.append('coverImage', coverImage);
    }

    const response = await apiClient.post<BookDto>(
      `${BOOKS_EXTN_API}/upload-pdf`,
      formData,
      {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
        // Track upload progress
        onUploadProgress: (progressEvent) => {
          if (progressEvent.total) {
            const percentCompleted = Math.round(
              (progressEvent.loaded * 100) / progressEvent.total
            );
            console.log(`Upload progress: ${percentCompleted}%`);
          }
        },
      }
    );

    return response.data;
  },

  /**
   * Upload EPUB file asynchronously (queue-based, non-blocking)
   * Returns immediately with a tracking ID for status polling
   * File is uploaded to blob storage and queued for background processing
   */
  async uploadEpubAsync(data: BookUploadDto, file: File, coverImage?: File, onProgress?: (progress: number) => void): Promise<BookUploadResultDto> {
    const formData = new FormData();

    // Append metadata
    Object.entries(data).forEach(([key, value]) => {
      if (value !== undefined && value !== null) {
        formData.append(key, value.toString());
      }
    });

    // Append file
    formData.append('epubFile', file);
    if (coverImage) {
      formData.append('coverImage', coverImage);
    }

    const response = await apiClient.post<BookUploadResultDto>(
      `${BOOKS_EXTN_API}/upload-epub-async`,
      formData,
      {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
        // Track upload progress (file upload only, not processing)
        onUploadProgress: (progressEvent) => {
          if (progressEvent.total && onProgress) {
            const percentCompleted = Math.round(
              (progressEvent.loaded * 100) / progressEvent.total
            );
            onProgress(percentCompleted);
          }
        },
      }
    );

    return response.data;
  },

  /**
   * Upload PDF file asynchronously (queue-based, non-blocking)
   * Returns immediately with a tracking ID for status polling
   * File is uploaded to blob storage and queued for background processing
   */
  async uploadPdfAsync(data: BookUploadDto, file: File, coverImage?: File, onProgress?: (progress: number) => void): Promise<BookUploadResultDto> {
    const formData = new FormData();

    // Append metadata
    Object.entries(data).forEach(([key, value]) => {
      if (value !== undefined && value !== null) {
        formData.append(key, value.toString());
      }
    });

    // Append file
    formData.append('pdfFile', file);
    if (coverImage) {
      formData.append('coverImage', coverImage);
    }

    const response = await apiClient.post<BookUploadResultDto>(
      `${BOOKS_EXTN_API}/upload-pdf-async`,
      formData,
      {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
        // Track upload progress (file upload only, not processing)
        onUploadProgress: (progressEvent) => {
          if (progressEvent.total && onProgress) {
            const percentCompleted = Math.round(
              (progressEvent.loaded * 100) / progressEvent.total
            );
            onProgress(percentCompleted);
          }
        },
      }
    );

    return response.data;
  },

  /**
   * Get upload status by tracking ID
   * Use for polling async upload progress
   */
  async getUploadStatus(trackingId: string): Promise<BookUploadStatusDto> {
    const response = await apiClient.get<BookUploadStatusDto>(
      `${BOOKS_EXTN_API}/upload-status/${trackingId}`
    );
    return response.data;
  },

  /**
   * Get book metadata including chapter list
   * Used to display navigation
   */
  async getBookMetadata(bookId: string): Promise<BookMetadataDto> {
    const response = await apiClient.get<BookMetadataDto>(
      `${BOOKS_EXTN_API}/metadata/${bookId}`
    );
    return response.data;
  },

  /**
   * Get HTML content for a specific chapter
   * Used for CustomHtml renderer
   */
  async getChapterContent(
    input: GetChapterContentInput
  ): Promise<ChapterContentDto> {
    const response = await apiClient.post<ChapterContentDto>(
      `${BOOKS_EXTN_API}/chapter-content`,
      input
    );
    return response.data;
  },

  /**
   * Get chapter by index (decrypts unified encryption chunks)
   * Used for offline downloads with encrypted database chunks
   * Works with books using unified encryption system
   */
  async getChapterByIndex(bookId: string, chapterIndex: number): Promise<{ id: string; title: string; content: string; order: number }> {
    const response = await apiClient.get(
      `${BOOKS_EXTN_API}/chapters/${bookId}/${chapterIndex}`
    );
    return response.data;
  },

  /**
   * Get EPUB file as blob for EPUB.js renderer
   * Fetches the file with authentication headers, returns as ArrayBuffer
   * This allows EPUB.js to work with authenticated endpoints
   */
  async getEpubBlob(bookId: string): Promise<ArrayBuffer> {
    const response = await apiClient.get<ArrayBuffer>(
      `${BOOKS_EXTN_API}/stream-epub/${bookId}`,
      {
        responseType: 'arraybuffer',
      }
    );
    return response.data;
  },

  /**
   * Get PDF file as blob for PDF.js renderer
   * Fetches the decrypted file with authentication headers, returns as ArrayBuffer
   * This allows PDF.js to work with authenticated endpoints
   */
  async getPdfBlob(bookId: string): Promise<ArrayBuffer> {
    const response = await apiClient.get<ArrayBuffer>(
      `${BOOKS_EXTN_API}/stream-pdf/${bookId}`,
      {
        responseType: 'arraybuffer',
      }
    );
    return response.data;
  },

  /**
   * Get all books that the current user has access to (based on active licenses)
   * Used by My Library page to show user's licensed books
   * Requires ReadBook permission
   */
  async getMyLibrary(): Promise<BookDto[]> {
    const response = await apiClient.get<BookDto[]>(
      `${BOOKS_EXTN_API}/my-library`
    );
    return response.data;
  },

  /**
   * Search within a specific book
   * Returns matching pages/chapters with highlighted excerpts
   * Requires ReadBook permission and active license
   */
  async searchInBook(bookId: string, query: string, maxResults: number = 50): Promise<BookSearchResultDto> {
    const response = await apiClient.get<BookSearchResultDto>(
      `${BOOKS_EXTN_API}/search-in-book`,
      {
        params: { bookId, query, maxResults }
      }
    );
    return response.data;
  },

  /**
   * Search across all books in user's library
   * Returns grouped results by book
   * Requires ReadBook permission
   */
  async searchAcrossBooks(query: string, maxResults: number = 100): Promise<CrossBookSearchResultDto> {
    const response = await apiClient.get<CrossBookSearchResultDto>(
      `${BOOKS_EXTN_API}/search-across-books`,
      {
        params: { query, maxResults }
      }
    );
    return response.data;
  },
};

// Named exports for convenience
export const { getAllBooks, uploadEpub, uploadPdf, uploadEpubAsync, uploadPdfAsync, getUploadStatus, getBookMetadata, getChapterContent, getChapterByIndex, getEpubBlob, getPdfBlob, getMyLibrary, searchInBook, searchAcrossBooks } = booksService;
